import { Inspector } from "./inspector";
import { listModules } from "./sim/modules";
import { markNetworksDirty } from "./sim/networks";
import { CELL_SIZE } from "./sim/ship";
import {
  canPlaceModule,
  effectiveCells,
  makeShipModule,
  recomputeIllegal,
  recomputeShipMass,
  rotateModuleCW,
  worldToShipGrid,
  worldToShipSubGrid,
} from "./sim/ship";
import type { Conduit, Ship, ShipModule, World } from "./sim/types";
import { getSprite } from "./sprites";
import { makePanelHeader } from "./util/panel";

type Tool = "place" | "delete" | "move" | "inspect";
const BRUSH_UNIVERSAL_CONDUIT = "__conduit_universal";
const BRUSH_HEAT_CONDUIT = "__conduit_heat";

export class BuildMode {
  private container: HTMLDivElement;
  private toggleBtn: HTMLButtonElement;
  private partsBtn: HTMLButtonElement;
  private deleteBtn: HTMLButtonElement;
  private moveBtn: HTMLButtonElement;
  private inspectBtn: HTMLButtonElement;
  private exportBtn: HTMLButtonElement;
  private root: HTMLDivElement;
  private listEl: HTMLDivElement;
  private statusEl: HTMLDivElement;
  private paletteRotateBtn: HTMLButtonElement;
  private floatingBar: HTMLDivElement;
  private floatingDelBtn: HTMLButtonElement;
  private floatingRotBtn: HTMLButtonElement;
  private exportPanel: HTMLDivElement | null = null;
  private exportArea: HTMLTextAreaElement | null = null;
  /** Keyed by brush id (defId or BRUSH_* sentinel). */
  private paletteSprites = new Map<string, HTMLDivElement>();
  /** Arrow overlays on heat-conduit palette items. */
  private paletteArrows = new Map<string, HTMLElement>();

  private active = false;
  private tool: Tool = "place";
  private brushDefId: string | null = null;
  private brushDefName: string | null = null;
  private brushFacing: 0 | 1 | 2 | 3 = 0;
  private deleteTarget: "module" | "conduit" = "module";

  /** Most recently placed/tapped module; green-ghost render target. */
  private activeModule: ShipModule | null = null;

  private dragModule: ShipModule | null = null;
  private dragOrigX = 0;
  private dragOrigY = 0;
  private dragAccumX = 0;
  private dragAccumY = 0;

  private painting = false;
  private lastPaintSub: { sgx: number; sgy: number } | null = null;

  /** View-space (world units). Caller rotates through cam.rot. */
  panX = 0;
  panY = 0;

  private onToggle: (active: boolean) => void = () => {};

  constructor(
    private ship: Ship,
    _world: World,
    private inspector: Inspector,
  ) {
    void _world;
    this.container = document.createElement("div");
    this.container.id = "build-toolbar";
    document.body.appendChild(this.container);

    this.toggleBtn  = this.makeButton("BILD", () => this.toggle());
    this.partsBtn   = this.makeButton("PART", () => this.openPalette());
    this.deleteBtn  = this.makeButton("ERAS", () => this.onDeleteBtn());
    this.moveBtn    = this.makeButton("MOVE", () => this.selectTool("move"));
    this.inspectBtn = this.makeButton("INFO", () => this.selectTool("inspect"));
    this.exportBtn  = this.makeButton("EXPO", () => this.openExport());

    this.partsBtn.style.display = "none";
    this.deleteBtn.style.display = "none";
    this.moveBtn.style.display = "none";
    this.inspectBtn.style.display = "none";
    this.exportBtn.style.display = "none";

    this.statusEl = document.createElement("div");
    this.statusEl.id = "build-status-floating";
    this.statusEl.style.display = "none";
    document.body.appendChild(this.statusEl);

    this.root = document.createElement("div");
    this.root.id = "build-panel";
    this.root.className = "panel build-panel";
    this.root.style.display = "none";

    const header = makePanelHeader("PARTS", () => this.closePalette());
    this.paletteRotateBtn = document.createElement("button");
    this.paletteRotateBtn.className = "palette-rotate-btn";
    this.paletteRotateBtn.title = "Rotate brush 90°";
    this.paletteRotateBtn.textContent = "↻ 0°";
    this.paletteRotateBtn.addEventListener("click", () => this.rotateBrush());
    header.insertBefore(this.paletteRotateBtn, header.lastChild);

    this.listEl = document.createElement("div");
    this.listEl.className = "build-list";

    this.root.appendChild(header);
    this.root.appendChild(this.listEl);
    document.body.appendChild(this.root);

    this.floatingBar = document.createElement("div");
    this.floatingBar.id = "build-floating-actions";
    this.floatingBar.style.display = "none";
    this.floatingDelBtn = document.createElement("button");
    this.floatingDelBtn.className = "floating-action-btn";
    this.floatingDelBtn.title = "Delete active part";
    this.floatingDelBtn.textContent = "×";
    this.floatingDelBtn.addEventListener("click", () => this.deleteActive());
    this.floatingRotBtn = document.createElement("button");
    this.floatingRotBtn.className = "floating-action-btn";
    this.floatingRotBtn.title = "Rotate active part 90°";
    this.floatingRotBtn.textContent = "↻";
    this.floatingRotBtn.addEventListener("click", () => this.rotateActive());
    this.floatingBar.appendChild(this.floatingDelBtn);
    this.floatingBar.appendChild(this.floatingRotBtn);
    document.body.appendChild(this.floatingBar);

    this.populate();
  }

  private makeButton(label: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.className = "toolbtn";
    b.textContent = label;
    b.addEventListener("click", onClick);
    this.container.appendChild(b);
    return b;
  }

  setOnToggle(fn: (active: boolean) => void): void {
    this.onToggle = fn;
  }

  isActive(): boolean {
    return this.active;
  }

  /** Current selection; passed through to pushShipTiles. */
  getActiveModule(): ShipModule | null {
    return this.active ? this.activeModule : null;
  }

  getTool(): Tool {
    return this.tool;
  }

  private setActive(m: ShipModule | null): void {
    this.activeModule = m;
    // ERAS is bulk-delete; no selection target.
    const showBar = m !== null && this.tool !== "delete";
    this.floatingBar.style.display = showBar ? "flex" : "none";
  }

  toggle(): void {
    if (this.active) {
      // Block exit while any placement is still a ghost.
      const ghosts = recomputeIllegal(this.ship);
      if (ghosts > 0) {
        this.flashStatus(`${ghosts} illegal placement${ghosts === 1 ? "" : "s"} — move or ERAS to leave build`);
        return;
      }
    }
    this.active = !this.active;
    if (this.active) {
      this.toggleBtn.textContent = "DONE";
      this.toggleBtn.classList.add("active");
      this.partsBtn.style.display = "";
      this.deleteBtn.style.display = "";
      this.moveBtn.style.display = "";
      this.inspectBtn.style.display = "";
      this.exportBtn.style.display = "";
      this.tool = "place";
      this.brushDefId = null;
      this.brushDefName = null;
      this.brushFacing = 0;
      this.setActive(null);
      this.dragModule = null;
      this.panX = 0;
      this.panY = 0;
      recomputeIllegal(this.ship);
      this.syncPaletteFacing();
      this.openPalette();
      this.syncHighlights();
    } else {
      recomputeShipMass(this.ship);

      this.toggleBtn.textContent = "BILD";
      this.toggleBtn.classList.remove("active");
      this.partsBtn.style.display = "none";
      this.deleteBtn.style.display = "none";
      this.moveBtn.style.display = "none";
      this.inspectBtn.style.display = "none";
      this.exportBtn.style.display = "none";
      if (this.exportPanel) this.exportPanel.style.display = "none";
      this.partsBtn.classList.remove("active");
      this.deleteBtn.classList.remove("active");
      this.moveBtn.classList.remove("active");
      this.inspectBtn.classList.remove("active");
      this.closePalette();
      this.statusEl.style.display = "none";
      this.brushDefId = null;
      this.brushDefName = null;
      this.setActive(null);
      this.dragModule = null;
      this.clearBrushSelection();
      this.inspector.hide();
    }
    this.onToggle(this.active);
  }

  handleWorldTap(wx: number, wy: number): boolean {
    if (!this.active) return false;

    const placingConduit = this.tool === "place"
      && (this.brushDefId === BRUSH_UNIVERSAL_CONDUIT
        || this.brushDefId === BRUSH_HEAT_CONDUIT);
    const deletingConduit = this.tool === "delete" && this.deleteTarget === "conduit";

    if (placingConduit) {
      const { sgx, sgy } = worldToShipSubGrid(this.ship, wx, wy);
      const type = this.brushDefId === BRUSH_HEAT_CONDUIT ? "heat" : "universal";
      return this.handlePlaceConduit(sgx, sgy, type);
    }
    if (deletingConduit) {
      const { sgx, sgy } = worldToShipSubGrid(this.ship, wx, wy);
      return this.handleDeleteConduit(sgx, sgy);
    }

    const { gx, gy } = worldToShipGrid(this.ship, wx, wy);
    switch (this.tool) {
      case "place":   return this.handlePlace(gx, gy);
      case "delete":  return this.handleDelete(gx, gy);
      case "move":    return this.handleMoveTap(gx, gy);
      case "inspect": return this.handleInspect(gx, gy);
    }
  }

  pan(dxCss: number, dyCss: number, cssPxPerCell: number): void {
    const worldPerCss = CELL_SIZE / cssPxPerCell;
    this.panX -= dxCss * worldPerCss;
    this.panY -= dyCss * worldPerCss;
  }

  isDraggingModule(): boolean {
    return this.dragModule !== null;
  }

  canPaintOnDrag(): boolean {
    if (!this.active) return false;
    if (this.tool === "place"
        && (this.brushDefId === BRUSH_UNIVERSAL_CONDUIT
            || this.brushDefId === BRUSH_HEAT_CONDUIT)) return true;
    if (this.tool === "delete" && this.deleteTarget === "conduit") return true;
    return false;
  }

  isPainting(): boolean {
    return this.painting;
  }

  beginPaintStroke(wx: number, wy: number): void {
    if (!this.canPaintOnDrag()) return;
    this.painting = true;
    this.lastPaintSub = null;
    this.paintAt(wx, wy);
  }

  paintAt(wx: number, wy: number): void {
    if (!this.painting) return;
    const { sgx, sgy } = worldToShipSubGrid(this.ship, wx, wy);
    if (this.lastPaintSub && this.lastPaintSub.sgx === sgx && this.lastPaintSub.sgy === sgy) {
      return;
    }
    this.lastPaintSub = { sgx, sgy };
    if (this.tool === "delete") {
      this.handleDeleteConduit(sgx, sgy);
    } else if (this.tool === "place") {
      const type = this.brushDefId === BRUSH_HEAT_CONDUIT ? "heat" : "universal";
      this.handlePlaceConduit(sgx, sgy, type);
    }
  }

  endPaintStroke(): void {
    this.painting = false;
    this.lastPaintSub = null;
  }

  beginDrag(wx: number, wy: number): void {
    if (!this.active || this.tool !== "move") return;
    const { gx, gy } = worldToShipGrid(this.ship, wx, wy);
    const m = this.topmostModuleAtGrid(gx, gy);
    if (!m) return;
    this.dragModule = m;
    this.dragOrigX = m.gx;
    this.dragOrigY = m.gy;
    this.dragAccumX = 0;
    this.dragAccumY = 0;
    this.setActive(m);
    this.flashStatus(`moving ${m.def.name}`);
  }

  dragBy(worldDx: number, worldDy: number): void {
    if (!this.dragModule) return;
    this.dragAccumX += worldDx;
    this.dragAccumY += worldDy;
    const dgx = Math.round(this.dragAccumX / CELL_SIZE);
    const dgy = Math.round(this.dragAccumY / CELL_SIZE);
    const targetX = this.dragOrigX + dgx;
    const targetY = this.dragOrigY + dgy;
    if (targetX === this.dragModule.gx && targetY === this.dragModule.gy) return;

    this.dragModule.gx = targetX;
    this.dragModule.gy = targetY;
    // Networks stay dirty until endDrag to avoid per-frame rebuild.
    recomputeIllegal(this.ship);
  }

  endDrag(): void {
    if (!this.dragModule) return;
    const def = this.dragModule.def;
    markNetworksDirty(this.ship);
    const illegalCount = recomputeIllegal(this.ship);
    const tag = this.dragModule.illegal
      ? "ghost"
      : illegalCount > 0 ? `OK · ${illegalCount} ghost left` : "OK";
    this.flashStatus(`${def.name} @ (${this.dragModule.gx}, ${this.dragModule.gy}) — ${tag}`);
    this.dragModule = null;
  }

  openExport(): void {
    if (!this.active) return;
    if (!this.exportPanel) this.buildExportPanel();
    if (!this.exportPanel || !this.exportArea) return;

    const text = serializeBlueprint(this.ship);
    this.exportArea.value = text;
    this.exportPanel.style.display = "flex";
    this.exportArea.focus();
    this.exportArea.select();
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  }

  private buildExportPanel(): void {
    const panel = document.createElement("div");
    panel.id = "export-panel";
    panel.className = "panel export-panel";
    panel.style.display = "none";

    const header = makePanelHeader("EXPORT BLUEPRINT", () => { panel.style.display = "none"; });

    const area = document.createElement("textarea");
    area.className = "export-textarea";
    area.readOnly = true;
    area.spellcheck = false;

    const copyRow = document.createElement("div");
    copyRow.className = "export-row";
    const hint = document.createElement("span");
    hint.className = "export-hint";
    hint.textContent = "Auto-copied. Paste into src/sim/blueprints/junker-01.ts.";
    const copyBtn = document.createElement("button");
    copyBtn.className = "toolbtn";
    copyBtn.textContent = "COPY";
    copyBtn.addEventListener("click", () => {
      area.focus();
      area.select();
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(area.value).catch(() => {});
      } else {
        document.execCommand?.("copy");
      }
      hint.textContent = "Copied!";
    });
    copyRow.appendChild(hint);
    copyRow.appendChild(copyBtn);

    panel.appendChild(header);
    panel.appendChild(area);
    panel.appendChild(copyRow);
    document.body.appendChild(panel);

    this.exportPanel = panel;
    this.exportArea = area;
  }

  private rotateBrush(): void {
    if (!this.active) return;
    this.brushFacing = ((this.brushFacing + 1) % 4) as 0 | 1 | 2 | 3;
    this.syncPaletteFacing();
    if (this.brushDefId) {
      const label = this.brushDefId === BRUSH_HEAT_CONDUIT
        ? "heat pipe"
        : (this.brushDefName ?? "brush");
      this.flashStatus(`${label} facing: ${this.brushFacing * 90}°`);
    }
  }

  private rotateActive(): void {
    if (!this.active) return;
    const m = this.activeModule;
    if (!m) return;
    const def = m.def;
    rotateModuleCW(this.ship, m);
    recomputeIllegal(this.ship);
    this.flashStatus(`rotated ${def.name} → ${m.facing * 90}°`);
  }

  private deleteActive(): void {
    if (!this.active) return;
    const m = this.activeModule;
    if (!m) return;
    const idx = this.ship.modules.indexOf(m);
    if (idx < 0) { this.setActive(null); return; }
    const def = m.def;
    this.ship.modules.splice(idx, 1);
    markNetworksDirty(this.ship);
    this.setActive(null);
    const illegalCount = recomputeIllegal(this.ship);
    this.flashStatus(
      illegalCount > 0
        ? `removed ${def.name} — ${illegalCount} ghost left`
        : `removed ${def.name}`,
    );
  }

  private onDeleteBtn(): void {
    if (this.tool === "delete") {
      this.deleteTarget = this.deleteTarget === "module" ? "conduit" : "module";
      this.updateStatusForTool();
      this.syncHighlights();
      return;
    }
    this.selectTool("delete");
  }

  private handlePlace(gx: number, gy: number): boolean {
    if (!this.brushDefId) {
      this.flashStatus("no brush — tap PART");
      return true;
    }
    const overlaps = !canPlaceModule(this.ship, this.brushDefId, gx, gy, this.brushFacing);
    const m = makeShipModule(this.brushDefId, gx, gy, this.brushFacing);
    this.ship.modules.push(m);
    markNetworksDirty(this.ship);
    const illegalCount = recomputeIllegal(this.ship);
    this.setActive(m);
    if (m.illegal) {
      const reason = overlaps ? "overlaps" : "not connected";
      this.flashStatus(`+ ${this.brushDefName} @ (${gx}, ${gy}) — ghost (${reason})`);
    } else {
      this.flashStatus(
        illegalCount > 0
          ? `+ ${this.brushDefName} @ (${gx}, ${gy}) — ${illegalCount} ghost left`
          : `+ ${this.brushDefName} @ (${gx}, ${gy})`,
      );
    }
    return true;
  }

  private handlePlaceConduit(sgx: number, sgy: number, type: "universal" | "heat"): boolean {
    const existing = this.ship.conduits.find(
      (c) => c.sgx === sgx && c.sgy === sgy && c.type === type,
    );
    if (existing) {
      this.flashStatus(`sub(${sgx}, ${sgy}) — ${type} conduit already here`);
      return true;
    }
    const cd: Conduit = { sgx, sgy, type, facing: type === "heat" ? this.brushFacing : 0 };
    this.ship.conduits.push(cd);
    markNetworksDirty(this.ship);
    this.setActive(null);
    const label = type === "heat" ? `heat pipe ${cd.facing * 90}°` : "conduit";
    this.flashStatus(`+ ${label} @ sub(${sgx}, ${sgy})`);
    return true;
  }

  private handleDeleteConduit(sgx: number, sgy: number): boolean {
    const before = this.ship.conduits.length;
    this.ship.conduits = this.ship.conduits.filter(
      (c) => !(c.sgx === sgx && c.sgy === sgy),
    );
    const removed = before - this.ship.conduits.length;
    if (removed === 0) {
      this.flashStatus(`sub(${sgx}, ${sgy}) — no conduit`);
      return true;
    }
    markNetworksDirty(this.ship);
    this.flashStatus(`removed ${removed} conduit${removed > 1 ? "s" : ""} @ sub(${sgx}, ${sgy})`);
    return true;
  }

  private topmostModuleAtGrid(gx: number, gy: number): ShipModule | null {
    const mods = this.ship.modules;
    for (let i = mods.length - 1; i >= 0; i--) {
      const m = mods[i];
      const eff = effectiveCells(m.def, m.facing);
      if (gx >= m.gx && gx < m.gx + eff.w && gy >= m.gy && gy < m.gy + eff.h) {
        return m;
      }
    }
    return null;
  }

  private handleDelete(gx: number, gy: number): boolean {
    // Reverse scan: ghosts are placed last → remove ghost first.
    const m = this.topmostModuleAtGrid(gx, gy);
    if (!m) {
      this.flashStatus(`(${gx}, ${gy}) — empty`);
      return true;
    }
    const def = m.def;
    this.ship.modules.splice(this.ship.modules.indexOf(m), 1);
    markNetworksDirty(this.ship);
    if (this.activeModule === m) this.setActive(null);
    const illegalCount = recomputeIllegal(this.ship);
    this.flashStatus(
      illegalCount > 0
        ? `removed ${def.name} — ${illegalCount} ghost left`
        : `removed ${def.name} @ (${m.gx}, ${m.gy})`,
    );
    return true;
  }

  private handleMoveTap(gx: number, gy: number): boolean {
    const m = this.topmostModuleAtGrid(gx, gy);
    if (!m) {
      this.flashStatus(`(${gx}, ${gy}) — empty`);
      this.setActive(null);
      return true;
    }
    this.setActive(m);
    const def = m.def;
    this.flashStatus(`selected ${def.name} — drag to move, or use ↻/× below`);
    return true;
  }

  private handleInspect(gx: number, gy: number): boolean {
    const m = this.topmostModuleAtGrid(gx, gy);
    if (!m) {
      this.flashStatus(`(${gx}, ${gy}) — empty`);
      this.inspector.hide();
      this.setActive(null);
      return true;
    }
    this.setActive(m);
    this.inspector.showModule(m);
    return true;
  }

  private selectTool(tool: Tool): void {
    if (this.tool === tool) return;
    this.tool = tool;
    this.dragModule = null;
    if (tool !== "place") {
      this.brushDefId = null;
      this.brushDefName = null;
      this.clearBrushSelection();
    }
    if (tool !== "inspect") this.inspector.hide();
    if (tool === "delete") this.setActive(null);
    else this.setActive(this.activeModule);
    this.syncHighlights();
    this.updateStatusForTool();
  }

  private syncHighlights(): void {
    this.partsBtn.classList.toggle("active",   this.tool === "place");
    this.deleteBtn.classList.toggle("active",  this.tool === "delete");
    this.moveBtn.classList.toggle("active",    this.tool === "move");
    this.inspectBtn.classList.toggle("active", this.tool === "inspect");
  }

  private updateStatusForTool(): void {
    switch (this.tool) {
      case "place":
        this.flashStatus(
          this.brushDefId
            ? `PLACE: ${this.brushDefName}`
            : "PLACE — open PART to pick a brush",
        );
        break;
      case "delete":
        this.flashStatus(this.deleteTarget === "conduit"
          ? "ERASE CONDUIT — tap to remove a conduit (tap ERAS again to switch)"
          : "ERASE — tap a module to remove (tap ERAS again to switch to conduits)");
        break;
      case "move":
        this.flashStatus("MOVE — drag a module to relocate");
        break;
      case "inspect":
        this.flashStatus("INFO — tap a module to view its stats");
        break;
    }
  }

  private openPalette(): void {
    this.root.style.display = "flex";
    this.statusEl.style.display = "none";
  }

  private closePalette(): void {
    this.root.style.display = "none";
    if (this.active) this.updateStatusForTool();
  }

  private flashStatus(msg: string): void {
    this.statusEl.textContent = msg;
    this.statusEl.style.display = this.root.style.display === "flex" ? "none" : "block";
  }

  private clearBrushSelection(): void {
    for (const child of Array.from(this.listEl.children)) {
      (child as HTMLElement).classList.remove("selected");
    }
  }

  private populate(): void {
    this.addConduitBrush(
      BRUSH_UNIVERSAL_CONDUIT,
      "Universal Conduit",
      "conduit_universal",
      "1×1 · routes power · fuel · cargo",
    );
    this.addConduitBrush(
      BRUSH_HEAT_CONDUIT,
      "Heat Pipe (arrow)",
      "conduit_heat",
      "1×1 · pumps heat in arrow dir (↻)",
    );

    for (const def of listModules()) {
      const item = document.createElement("button");
      item.className = "build-item";
      item.dataset.defId = def.id;

      const sprite = document.createElement("div");
      sprite.className = "build-item-sprite";
      try {
        sprite.innerHTML = getSprite(def.sprite).svg;
      } catch {}
      this.paletteSprites.set(def.id, sprite);

      const label = document.createElement("div");
      label.className = "build-item-label";
      label.innerHTML =
        `<span class="bi-name">${def.name}</span>` +
        `<span class="bi-meta">T${def.tier} · ${def.cells.w}×${def.cells.h} · ${(def.stats.mass / 1000).toFixed(1)}t</span>`;

      item.appendChild(sprite);
      item.appendChild(label);

      item.addEventListener("click", () => {
        this.brushDefId = def.id;
        this.brushDefName = def.name;
        this.brushFacing = 0;
        this.clearBrushSelection();
        item.classList.add("selected");
        this.tool = "place";
        this.syncHighlights();
        this.syncPaletteFacing();
        this.closePalette();
      });

      this.listEl.appendChild(item);
    }
    this.syncPaletteFacing();
  }

  /** Spin inner SVGs (negate: CSS +Y down vs renderer's math convention). */
  private syncPaletteFacing(): void {
    const deg = this.brushFacing * 90;
    const transform = deg === 0 ? "" : `rotate(${-deg}deg)`;
    for (const sprite of this.paletteSprites.values()) {
      const svg = sprite.querySelector(":scope > svg");
      if (svg instanceof SVGElement) svg.style.transform = transform;
    }
    for (const arrow of this.paletteArrows.values()) {
      const svg = arrow.querySelector("svg");
      if (svg instanceof SVGElement) svg.style.transform = transform;
    }
    this.paletteRotateBtn.textContent = `↻ ${deg}°`;
  }

  private addConduitBrush(id: string, name: string, spriteKey: string, meta: string): void {
    const item = document.createElement("button");
    item.className = "build-item";
    item.dataset.defId = id;
    const sprite = document.createElement("div");
    sprite.className = "build-item-sprite";
    try { sprite.innerHTML = getSprite(spriteKey).svg; } catch {}
    if (id === BRUSH_HEAT_CONDUIT) {
      this.paletteSprites.set(id, sprite);
      const arrow = document.createElement("div");
      arrow.className = "build-item-arrow";
      try { arrow.innerHTML = getSprite("conduit_heat_arrow").svg; } catch {}
      sprite.appendChild(arrow);
      this.paletteArrows.set(id, arrow);
    }
    const label = document.createElement("div");
    label.className = "build-item-label";
    label.innerHTML =
      `<span class="bi-name">${name}</span>` +
      `<span class="bi-meta">${meta}</span>`;
    item.appendChild(sprite);
    item.appendChild(label);
    item.addEventListener("click", () => {
      this.brushDefId = id;
      this.brushDefName = name;
      this.brushFacing = 0;
      this.clearBrushSelection();
      item.classList.add("selected");
      this.tool = "place";
      this.syncHighlights();
      this.syncPaletteFacing();
      this.closePalette();
    });
    this.listEl.appendChild(item);
  }
}

function serializeBlueprint(ship: Ship): string {
  const ordered = [...ship.modules]
    .filter((m) => m.hp > 0)
    .sort((a, b) => a.gy - b.gy || a.gx - b.gx);
  const lines = ordered.map((m) => {
    const facing = m.facing ? `, facing: ${m.facing}` : "";
    return `    { defId: ${JSON.stringify(m.defId)}, gx: ${m.gx}, gy: ${m.gy}${facing} },`;
  });
  const conduits = [...ship.conduits].sort((a, b) =>
    a.sgy - b.sgy || a.sgx - b.sgx || a.type.localeCompare(b.type));
  const conduitLines = conduits.map((c) => {
    const facing = c.type === "heat" && c.facing ? `, facing: ${c.facing}` : "";
    return `    { sgx: ${c.sgx}, sgy: ${c.sgy}, type: ${JSON.stringify(c.type)}${facing} },`;
  });
  return [
    `export const JUNKER_01: ShipBlueprint = {`,
    `  name: ${JSON.stringify(ship.name)},`,
    `  modules: [`,
    ...lines,
    `  ],`,
    `  conduits: [`,
    ...conduitLines,
    `  ],`,
    `};`,
  ].join("\n");
}
